/**
 * SITE CONTENT EXPORT — documentation snapshot of the live website.
 *
 * This file is a READ-ONLY transcription of what already exists on the site.
 * It powers the /content-export documentation page and changes nothing about
 * the website itself. Wording is copied verbatim from the live components and
 * pages so the exported PDF is an accurate record for SEO/AEO analysis.
 */
export type ExportBlock = {
  type: 'p';
  text: string;
} | {
  type: 'h3';
  text: string;
} | {
  type: 'list';
  items: string[];
} | {
  type: 'kv';
  items: {
    label: string;
    value: string;
  }[];
} | {
  type: 'code';
  text: string;
};
export interface ExportSection {
  heading: string;
  /** The heading level used on the live site for this section, if it has one. */
  headingLevel?: string;
  blocks: ExportBlock[];
}
export interface ExportFaq {
  question: string;
  answer: string;
}
export interface ExportImage {
  src: string;
  alt: string;
  notes?: string;
}
export interface ExportPage {
  id: string;
  name: string;
  url: string;
  seoTitle: string;
  metaDescription: string;
  canonical: string;
  robots: string;
  openGraph: {
    label: string;
    value: string;
  }[];
  schema: string;
  h1: string;
  sections: ExportSection[];
  faqs?: ExportFaq[];
  faqNote?: string;
  images?: ExportImage[];
  internalLinks?: string[];
  externalLinks?: string[];
}
export const SITE_OVERVIEW = {
  businessName: "Mike's Pro Detail",
  businessType: 'Mobile car detailing (fully mobile — no storefront)',
  domain: 'https://www.fulldetailpro.com',
  phone: '(818) 381-9905',
  telLink: 'tel:8183819905',
  primaryMarkets: 'Ventura County & San Fernando Valley, California',
  framework: 'React 18 + TypeScript single-page app, react-router-dom, Tailwind CSS',
  rendering: 'Client-side rendered. Head tags and JSON-LD are injected per route on mount.',
  analytics: 'Google Tag Manager (GTM-PTM9WZ8X), Google Analytics 4 (G-BBSL5F5SDB), Google Ads (AW-349742034)',
  formHandler: 'Web3Forms (contact form submissions)'
};
export const ROBOTS_TXT = `User-agent: *
Allow: /

Sitemap: https://www.fulldetailpro.com/sitemap.xml`;
export const SITEMAP_URLS = ['https://www.fulldetailpro.com/', 'https://www.fulldetailpro.com/services/mobile-detailing', 'https://www.fulldetailpro.com/services/full-car-detail', 'https://www.fulldetailpro.com/services/interior-detailing', 'https://www.fulldetailpro.com/services/exterior-detailing', 'https://www.fulldetailpro.com/services/ceramic-coating', 'https://www.fulldetailpro.com/services/headlight-restoration', 'https://www.fulldetailpro.com/services/engine-bay-detailing', 'https://www.fulldetailpro.com/ventura-county/camarillo', 'https://www.fulldetailpro.com/ventura-county/oxnard', 'https://www.fulldetailpro.com/ventura-county/ventura', 'https://www.fulldetailpro.com/ventura-county/thousand-oaks', 'https://www.fulldetailpro.com/ventura-county/simi-valley', 'https://www.fulldetailpro.com/ventura-county/moorpark', 'https://www.fulldetailpro.com/ventura-county/newbury-park', 'https://www.fulldetailpro.com/ventura-county/westlake-village'];
export const ROUTE_TABLE = [{
  path: '/',
  page: 'Home'
}, {
  path: '/services/mobile-detailing',
  page: 'Mobile Detailing'
}, {
  path: '/services/interior-detailing',
  page: 'Interior Detailing'
}, {
  path: '/services/exterior-detailing',
  page: 'Exterior Detailing'
}, {
  path: '/services/full-car-detail',
  page: 'Full Car Detail'
}, {
  path: '/services/ceramic-coating',
  page: 'Ceramic Coating'
}, {
  path: '/services/headlight-restoration',
  page: 'Headlight Restoration'
}, {
  path: '/services/engine-bay-detailing',
  page: 'Engine Bay Detailing'
}, {
  path: '/ventura-county/:slug',
  page: 'City / service-area page (8 cities)'
}, {
  path: '*',
  page: '404 Not Found (noindex, follow)'
}];
/* ---------------------------------------------------------------- */
/* GLOBAL / SITE-WIDE UI                                             */
/* ---------------------------------------------------------------- */
export const PROMO_BAR_TEXT = '🔥 Limited Time Only! $50 OFF Listed Price of Full Details & $20 OFF Wash & Wax — Call Now!';
export const NAVIGATION_ITEMS = [{
  label: 'Mike\u2019s Pro Detail (logo / home link)',
  target: '/'
}, {
  label: 'Services',
  target: '/#services'
}, {
  label: 'About',
  target: '/#about'
}, {
  label: 'Gallery',
  target: '/#gallery'
}, {
  label: 'Reviews',
  target: '/#reviews'
}, {
  label: 'FAQ',
  target: '/#faq'
}, {
  label: 'Contact Us',
  target: '/#contact-form'
}, {
  label: '(818) 381-9905',
  target: 'tel:8183819905'
}];
export const FOOTER_COLUMNS = [{
  heading: 'Detailing Services',
  links: [{
    label: 'Mobile Detailing',
    target: '/services/mobile-detailing'
  }, {
    label: 'Interior Detailing',
    target: '/services/interior-detailing'
  }, {
    label: 'Exterior Detailing',
    target: '/services/exterior-detailing'
  }, {
    label: 'Full Car Detail',
    target: '/services/full-car-detail'
  }, {
    label: 'Ceramic Coating',
    target: '/services/ceramic-coating'
  }]
}, {
  heading: 'Service Areas',
  links: [{
    label: 'Camarillo',
    target: '/ventura-county/camarillo'
  }, {
    label: 'Oxnard',
    target: '/ventura-county/oxnard'
  }, {
    label: 'Ventura',
    target: '/ventura-county/ventura'
  }, {
    label: 'Thousand Oaks',
    target: '/ventura-county/thousand-oaks'
  }, {
    label: 'Simi Valley',
    target: '/ventura-county/simi-valley'
  }]
}, {
  heading: 'Find Us Online',
  links: [{
    label: 'Google Maps & Reviews',
    target: 'https://www.google.com/maps/search/Mike%27s+Pro+Detail+Ventura+County (external)'
  }, {
    label: 'Better Business Bureau',
    target: 'https://www.bbb.org/search?find_text=Mike%27s+Pro+Detail&find_loc=Ventura+County%2C+CA (external)'
  }]
}];
export const FOOTER_BOTTOM = '© [current year] Mike\u2019s Pro Detail — Mobile Car Detailing Ventura County & San Fernando Valley. All rights reserved.  ·  Services · Service Areas · FAQ';
/* ---------------------------------------------------------------- */
/* HOMEPAGE                                                          */
/* ---------------------------------------------------------------- */
export const HOME_PAGE: ExportPage = {
  id: 'home',
  name: 'Homepage',
  url: 'https://www.fulldetailpro.com/',
  seoTitle: "Mobile Detailing Ventura County | Mobile Car Detailing | Mike's Pro Detail",
  metaDescription: 'Mobile detailing in Ventura County. Mike’s Pro Detail brings 5-star car detailing to your door in Camarillo, Oxnard, Ventura, Thousand Oaks & more. Call (818) 381-9905.',
  canonical: 'https://www.fulldetailpro.com/',
  robots: 'index, follow, max-image-preview:large, max-snippet:-1',
  openGraph: [{
    label: 'og:type',
    value: 'website'
  }, {
    label: 'og:site_name',
    value: "Mike's Pro Detail"
  }, {
    label: 'og:locale',
    value: 'en_US'
  }, {
    label: 'og:url',
    value: 'https://www.fulldetailpro.com/'
  }, {
    label: 'og:title',
    value: "Mobile Detailing Ventura County | Mobile Car Detailing | Mike's Pro Detail"
  }, {
    label: 'og:description',
    value: 'Mobile detailing in Ventura County. Mike’s Pro Detail brings 5-star car detailing to your door in Camarillo, Oxnard, Ventura, Thousand Oaks & more. Call (818) 381-9905.'
  }, {
    label: 'og:image',
    value: 'https://cdn.magicpatterns.com/uploads/8ujQRT1VS2FomWSs5Jvzhk/mikesprodetail2026logo.png'
  }, {
    label: 'twitter:card',
    value: 'summary_large_image'
  }, {
    label: 'twitter:title / twitter:description / twitter:url / twitter:image',
    value: 'Mirror the Open Graph values above'
  }],
  schema: 'JSON-LD @graph containing: LocalBusiness + AutoRepair (@id .../#business), WebSite (@id .../#website), WebPage (@id .../#webpage), FAQPage (@id .../#faq)',
  h1: 'Mobile Detailing Near You',
  sections: [{
    heading: 'Hero Section (#home)',
    headingLevel: 'H1 + H2',
    blocks: [{
      type: 'kv',
      items: [{
        label: 'H1',
        value: 'Mobile Detailing Near You'
      }, {
        label: 'H2',
        value: 'Ventura County & San Fernando Valley'
      }, {
        label: 'Phone (large clickable)',
        value: '(818) 381-9905'
      }]
    }, {
      type: 'p',
      text: '5-Star Rated Auto Detailing Service — Camarillo, Thousand Oaks, Ventura & All of Ventura County'
    }, {
      type: 'p',
      text: 'Professional mobile car detailing at your home or office. No drop-off needed.'
    }, {
      type: 'kv',
      items: [{
        label: 'Call to action',
        value: 'Book Now (scrolls to #contact)'
      }, {
        label: 'Desktop phone behavior',
        value: 'Clicking the phone number copies it to the clipboard and shows "Phone number copied to clipboard!"'
      }]
    }]
  }, {
    heading: 'Pricing — "Mobile Car Detailing Packages & Pricing" (#services)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Transparent pricing for professional mobile car detailing in Ventura County. Every service includes premium eco-friendly products, expert care, and the convenience of at-your-door service throughout Camarillo, Thousand Oaks, and beyond.'
    }, {
      type: 'h3',
      text: 'Basic Wash — SM/MID $90 · SUV/CROSS $105'
    }, {
      type: 'p',
      text: '2 Car Minimum Basic Washes Only'
    }, {
      type: 'list',
      items: ['Exterior Wash', 'Light Wipe Down', 'Vacuum', 'Clean Rims', 'Dress Tires', 'Clean Windows']
    }, {
      type: 'h3',
      text: 'Wash & Wax — SM/MID $160 · SUV/CROSS $180'
    }, {
      type: 'p',
      text: 'A Wash and Wax cleans your car, adds a protective layer, helps paint stay shiny, repel water, and dirt.'
    }, {
      type: 'list',
      items: ['Exterior Wash', 'Window Cleaning', 'Vacuum', 'Light Wipedown Interior', 'Tire Dressing', 'Hybrid Ceramic Wax']
    }, {
      type: 'h3',
      text: 'Full Detail (MOST POPULAR) — SM/MID $300 · SUV/CROSS $350'
    }, {
      type: 'p',
      text: 'Our most popular mobile car detailing package — complete interior and exterior restoration for a showroom finish'
    }, {
      type: 'list',
      items: ['Includes Interior Detail', 'Exterior Wash', 'Clay Bar Treatment', 'Clean Rims', 'Dress Tires', 'Hybrid Ceramic Wax', 'Deep Vacuum']
    }, {
      type: 'h3',
      text: 'Interior Detail — SM/MID $180 · SUV/CROSS $195'
    }, {
      type: 'p',
      text: 'Professional interior car cleaning service — deep clean, shampoo, steam sanitize every surface inside your vehicle'
    }, {
      type: 'list',
      items: ['Shampoo Seats', 'Deep Clean Interior', 'Vacuum', 'Shampoo Floor Mats', 'Steam & Sanitize']
    }]
  }, {
    heading: 'Extra Services (#extras)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Add these to any package for the ultimate finish'
    }, {
      type: 'kv',
      items: [{
        label: 'Engine Bay Top',
        value: '$120'
      }, {
        label: 'Headlight Restore',
        value: '$80'
      }, {
        label: 'Clay Bar Treatment',
        value: '$60'
      }, {
        label: 'Water Spots / Dog Hair',
        value: 'Call for Pricing'
      }, {
        label: 'Ceramic Coating Glass',
        value: '$200'
      }, {
        label: 'Ceramic Coating — 1 Year',
        value: 'Call for Pricing'
      }]
    }]
  }, {
    heading: 'Our Detailing Services (#service-details)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Professional mobile auto detailing services delivered to your driveway in Ventura County and the San Fernando Valley'
    }, {
      type: 'h3',
      text: 'Interior Detail — Starting at $180'
    }, {
      type: 'p',
      text: 'Deep clean, shampoo, and steam sanitize every surface inside your vehicle. Serving Camarillo, Thousand Oaks, and all of Ventura County with premium interior detailing at your door.'
    }, {
      type: 'h3',
      text: 'Exterior Detail — Starting at $160'
    }, {
      type: 'p',
      text: 'Hand wash, clay bar treatment, and paint protection for a showroom-quality finish. Professional mobile exterior detailing throughout Ventura County and the San Fernando Valley.'
    }, {
      type: 'h3',
      text: 'Full Car Detail — Starting at $300'
    }, {
      type: 'p',
      text: 'Our most popular package — complete interior and exterior restoration with hybrid ceramic wax. The best full mobile car detail in Camarillo, Simi Valley, and Ventura County.'
    }, {
      type: 'h3',
      text: 'Hybrid Ceramic Wax — Call for Quote'
    }, {
      type: 'p',
      text: 'Long-lasting ceramic protection that shields your paint from UV rays, dirt, and contaminants. Professional-coating applied at your home or office in Ventura County.'
    }, {
      type: 'h3',
      text: 'Headlight Restoration — From $80'
    }, {
      type: 'p',
      text: 'Restore cloudy, yellowed headlights to crystal-clear condition for improved visibility and appearance. Mobile headlight restoration serving Calabasas, Woodland Hills, and Ventura County.'
    }, {
      type: 'kv',
      items: [{
        label: 'Calls to action per card',
        value: '"Learn More" (links to the service page) and "Book Now" (scrolls to #contact)'
      }]
    }]
  }, {
    heading: "Ventura County's Trusted Mobile Car Detailing Service (#about)",
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: "Looking for professional mobile car detailing in Ventura County or San Fernando Valley? Mike's Pro Detail is a premium, fully mobile auto detailing service proudly serving Camarillo, Thousand Oaks, Ventura, Oxnard, Agoura Hills, Calabasas, Woodland Hills, Westlake Village, and surrounding communities. We bring professional-grade car detailing directly to your home, office, or anywhere that's convenient for you — no drop-off required."
    }, {
      type: 'p',
      text: 'Owner-operated by Mike, every vehicle receives personal, hands-on attention using only premium, eco-friendly products. From deep interior car cleaning and seat shampooing to clay bar treatments, hybrid ceramic wax protection, and full car detail services, we deliver showroom-quality results at your doorstep. We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.'
    }, {
      type: 'p',
      text: "Rated 5 stars on Google and Yelp, Mike's Pro Detail has earned a loyal customer base across Ventura County through honest pricing, reliable scheduling, and results that speak for themselves. Whether you need a quick wash and wax in Camarillo, a full car detail in Thousand Oaks, or interior detailing in Woodland Hills, we treat every vehicle like it's our own."
    }]
  }, {
    heading: 'Mobile Car Detailing Service Areas (#service-areas)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Professional mobile car detailing throughout Ventura County and San Fernando Valley — we bring the detail shop to your driveway'
    }, {
      type: 'list',
      items: ['Camarillo', 'Thousand Oaks', 'Oxnard', 'Ventura', 'Agoura Hills', 'Calabasas', 'Woodland Hills', 'Westlake Village', 'Newbury Park', 'Oak Park', 'Simi Valley', 'Moorpark', 'Encino', 'Tarzana', 'Sherman Oaks', 'Chatsworth', 'Malibu']
    }, {
      type: 'p',
      text: "Don't see your area? Call (818) 381-9905 — we may still be able to come to you!"
    }]
  }, {
    heading: 'Mobile Detailing in Ventura County, Done Right (long-form SEO content block)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: "Mike's Pro Detail is a premium mobile car detailing company proudly serving Ventura County, California. We bring a fully equipped, professional detail shop directly to your driveway, so you get a flawless, showroom-quality finish without ever leaving home. From mobile detailing in Camarillo and car detailing in Oxnard to auto detailing in Ventura, our mobile team comes to you with our own water, detailing equipment, and premium eco-friendly products. There is no drop-off, no waiting room, and no compromise on quality."
    }, {
      type: 'p',
      text: 'Whether you searched for "car detailing near me", "mobile car detailing Ventura County", or a specific service in your city, you have found the most trusted name in local auto detailing. We treat every vehicle as if it were our own, combining meticulous hand work with professional-grade equipment to deliver results that protect your investment and turn heads.'
    }, {
      type: 'h3',
      text: 'H2 — Why Choose Mobile Detailing?'
    }, {
      type: 'p',
      text: 'Traditional car washes are fast but harsh — automated brushes and recycled water can leave swirl marks and scratches in your clear coat. Professional mobile detailing is the opposite: a careful, hand-crafted process performed right where you live or work. Choosing mobile detailing in Ventura County gives you several major advantages:'
    }, {
      type: 'list',
      items: ['Convenience: We come to your home, office, or apartment. You reclaim hours of your day instead of sitting at a shop.', 'Quality: Hand washing, clay bar decontamination, and careful interior work that automated washes simply cannot match.', 'We bring the equipment: We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.', 'Protection: Premium waxes, sealants, and ceramic coatings that shield your paint from the Southern California sun and coastal salt air.']
    }, {
      type: 'h3',
      text: 'H2 — Our Professional Detailing Services'
    }, {
      type: 'p',
      text: "We offer a complete range of mobile detailing services for cars, trucks, and SUVs across Ventura County. Each service is performed on-site and tailored to your vehicle's specific needs."
    }, {
      type: 'h3',
      text: 'H3 — Interior Detailing'
    }, {
      type: 'p',
      text: 'Our interior detailing service deep cleans, shampoos, steam sanitizes, and conditions every surface inside your cabin. We remove embedded dirt, pet hair, stains, and odors, restoring a fresh, like-new feel while improving the air quality you breathe on every drive.'
    }, {
      type: 'h3',
      text: 'H3 — Exterior Detailing'
    }, {
      type: 'p',
      text: 'Our exterior detailing service includes a safe hand wash, clay bar treatment, wheel and tire cleaning, and a durable wax or sealant. This safely removes bonded contaminants, restores deep gloss, and protects your paint from UV damage and oxidation.'
    }, {
      type: 'h3',
      text: 'H3 — Full Car Detail'
    }, {
      type: 'p',
      text: "Our most popular package, the full car detail, combines our complete interior and exterior services for a true bumper-to-bumper transformation. It is the most cost-effective way to completely reset your vehicle's condition inside and out."
    }, {
      type: 'h3',
      text: 'H3 — Engine Bay Detailing'
    }, {
      type: 'p',
      text: "Our engine bay detailing safely degreases and cleans your engine compartment, making it easier to spot leaks, helping prevent heat-trapping grime, and dramatically improving your vehicle's presentation and resale value."
    }, {
      type: 'h3',
      text: 'H3 — Headlight Restoration'
    }, {
      type: 'p',
      text: "Cloudy, yellowed headlights are both an eyesore and a safety hazard. Our headlight restoration service permanently removes oxidation and restores crystal-clear lenses, improving your nighttime visibility and your car's appearance."
    }, {
      type: 'h3',
      text: 'H3 — Ceramic Coating'
    }, {
      type: 'p',
      text: 'For enthusiasts and luxury vehicle owners, our ceramic coating locks in a deep, glossy finish with years of durable, hydrophobic protection against UV rays, dirt, and contaminants.'
    }, {
      type: 'h3',
      text: 'H2 — Serving All of Ventura County'
    }, {
      type: 'p',
      text: 'As a mobile operation, we bring our detailing service to every community in Ventura County. Wherever you are, we come to you with a complete, professional detail. Explore our dedicated city pages for local information and pricing:'
    }, {
      type: 'list',
      items: ['Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks', 'Simi Valley', 'Moorpark', 'Newbury Park', 'Westlake Village']
    }, {
      type: 'p',
      text: 'We provide mobile detailing in Camarillo, car detailing in Oxnard, auto detailing in Ventura, detailing in Thousand Oaks, Simi Valley, Moorpark, Newbury Park, and Westlake Village. From the coast to the inland valleys, our mobile detailing service is never far away.'
    }, {
      type: 'h3',
      text: "H2 — Why Ventura County Trusts Mike's Pro Detail"
    }, {
      type: 'p',
      text: "With a 5-star reputation built across Ventura County, Mike's Pro Detail has earned the loyalty of drivers who demand the very best for their vehicles. Our success comes down to a simple philosophy: uncompromising quality, genuine convenience, and treating every customer's car with the same care we would give our own. We use only premium, eco-friendly products and professional-grade equipment, and we never rush a job — because a truly great detail takes time and attention."
    }, {
      type: 'p',
      text: 'Our experienced technicians understand the unique challenges of Southern California vehicles: relentless UV exposure that fades paint and cracks interiors, coastal salt air that accelerates corrosion, and fine inland dust that works its way into every surface. Every service we provide is designed to combat these conditions and keep your vehicle protected, beautiful, and holding its value for years to come.'
    }, {
      type: 'h3',
      text: 'H2 — How Our Mobile Detailing Process Works'
    }, {
      type: 'p',
      text: 'Booking is simple. Call or text Mike directly, describe your vehicle and the service you need, and we will provide a clear quote and find a time that works for you — including weekends. On the day of your appointment, our fully equipped mobile unit arrives at your home or office with everything required to complete the detail on-site. You stay productive or relax while we transform your vehicle, and you inspect the results before we leave. It is professional auto detailing made effortless.'
    }, {
      type: 'h3',
      text: 'H2 — Book Your Mobile Detail Today'
    }, {
      type: 'p',
      text: "Ready to experience the convenience and quality of mobile detailing in Ventura County? Whether you need a quick maintenance wash, a complete full detail, or specialty services like ceramic coating and headlight restoration, Mike's Pro Detail is ready to bring the shop to you. Call or text (818) 381-9905 today, or request a free quote online, and discover why drivers throughout Camarillo, Oxnard, Ventura, Thousand Oaks, and all of Ventura County trust us with their vehicles."
    }]
  }, {
    heading: '5-Star Mobile Detailing Reviews (#reviews)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: "See why Ventura County and San Fernando Valley trust Mike's Pro Detail for professional mobile car detailing"
    }, {
      type: 'h3',
      text: 'Erika D. — Yelp — 5 stars — Verified Customer'
    }, {
      type: 'p',
      text: "Mike is amazing at what he does. He has done my car, my husband's car, my sister's car, my sister-in-law and brother-in law's cars. All of us have been so happy. Mike is incredibly detailed, does an eco-friendly car wash and leaves your car looking so perfectly polished/pristine afterwards. Thank you Mike! We are so happy with your work and will continuously refer you to friends and family. :)"
    }, {
      type: 'h3',
      text: 'Juan R. — Yelp — 5 stars — Verified Customer'
    }, {
      type: 'p',
      text: "If you are looking for a good detailing for your car with the convenience of having it done at home, look no further than Mike's Pro Detail! Mike did a great job with our cars. He was responsive, communicated well, arrived on time, and got the job done. I've already used him several times! He's excellent at what he does."
    }, {
      type: 'h3',
      text: 'Diane B. — Yelp — 5 stars — Verified Customer'
    }, {
      type: 'p',
      text: "Mike has washed my cars multiple times. He makes scheduling and communicating easy. He is always friendly and responsive. He's handled tree sap on one car--which was challenging to remove--without extra charge. He is honest (no coins or mints filched) and a pleasure to deal with throughout. Final product is spectacular, without fail. I recommend him without reservation, and I'm a lawyer and fussy about my cars."
    }, {
      type: 'h3',
      text: 'Jessica M. — Google — 5 stars — Verified Customer'
    }, {
      type: 'p',
      text: "I had a great experience with Mike's Pro Detail! I had spilled a smoothie all over my car on the way home one evening - it was in every nook and cranny and was so bad that I couldn't get it out myself. So I had to find a professional to clean it all out before it started to make my car permanently smell of rotting fruit! I luckily found Mike and he was able to fit me in that next morning. He was so nice and helpful and did such a great job doing a deep clean - Thank you again Mike!"
    }, {
      type: 'h3',
      text: 'David L. — Google — 5 stars — Verified Customer'
    }, {
      type: 'p',
      text: "This is the second time that I have used Mike's services and I realize now that I should I have called him sooner! He took my black Lexus IS from a dirty, dingy, greyscale embarrassment to a piece of perfection with a showroom shine. Mike arrived early with all necessary equipment and supplies and went straight to work. He is the ultimate professional with great communication and flexibility in his schedule. Can't recommend his services enough! You will be impressed!"
    }, {
      type: 'h3',
      text: 'Holy B. — Google — 5 stars — Verified Customer'
    }, {
      type: 'p',
      text: "It was easy to set up an appointment and excellent communication. I had never used a mobile detail service and I was very pleased with Mike's service! My car was filthy from driving to Colorado and back and general lack of care. Mike made it look like new again with a full detail. He was very detailed and asked me to find areas that he could touch up once he was finished. Very professional, nice guy. Definitely would use him again."
    }, {
      type: 'p',
      text: 'Badge displayed below the reviews: "5.0 Average Rating across Google & Yelp"'
    }, {
      type: 'h3',
      text: 'H3 — Check Us Out'
    }, {
      type: 'p',
      text: 'Read more reviews on Google and Yelp to see why our customers trust us with their vehicles'
    }, {
      type: 'kv',
      items: [{
        label: 'View Google Reviews',
        value: 'https://share.google/k4UimfxXqCgBa74jP (external)'
      }, {
        label: 'View Yelp Reviews',
        value: 'https://www.yelp.com/biz/mikes-pro-detail-agoura (external)'
      }]
    }, {
      type: 'p',
      text: 'NOTE FOR ANALYSIS: These reviews are displayed as visible content only. No aggregateRating or Review structured data is emitted for them, because the ratings are not first-party verifiable review data.'
    }]
  }, {
    heading: 'Our Work — Before & After Detailing Results (#gallery)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'See the difference professional mobile car detailing makes — real results from real customers in Ventura County'
    }, {
      type: 'p',
      text: '12 photos in a clickable lightbox grid. Full alt text listed in the Images section for this page.'
    }]
  }, {
    heading: 'Frequently Asked Questions (#faq)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Accordion with 19 questions. Full text listed in the FAQ section below — these are also emitted as FAQPage structured data from the same source, so schema and visible content match exactly.'
    }]
  }, {
    heading: 'Contact Us (#contact-form)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Fill out the form and we will send you a custom quote.'
    }, {
      type: 'list',
      items: ['Name * (text)', 'Email * (email)', 'Phone * (tel)', 'Make / Model * (text, e.g. 2023 Tesla Model 3)', 'Where did you hear about us? (text — Google, Yelp, Friend, etc.)', 'Type of Service * (select: Basic Wash, Wash & Wax, Full Detail, Interior Detail, Engine Bay Cleaning, Others)', 'Message (textarea)', 'Submit button: "Send Message"']
    }, {
      type: 'kv',
      items: [{
        label: 'Success state',
        value: 'Message Sent! — Thank you for reaching out. Mike will get back to you shortly.'
      }, {
        label: 'Error state',
        value: 'Something went wrong. Please try again or call (818) 381-9905 directly.'
      }, {
        label: 'Submitting state',
        value: 'Sending…'
      }, {
        label: 'Handler',
        value: 'Web3Forms API'
      }]
    }]
  }, {
    heading: 'Ready for a Showroom Shine? (#contact)',
    headingLevel: 'H2',
    blocks: [{
      type: 'p',
      text: 'Book your mobile car detailing appointment today — serving Camarillo, Thousand Oaks, Simi Valley, Ventura, Agoura Hills, Calabasas Woodland Hills & Ventura County'
    }, {
      type: 'kv',
      items: [{
        label: 'Call or Text Mike',
        value: '(818) 381-9905'
      }, {
        label: 'Availability badge',
        value: 'Available Now'
      }]
    }]
  }],
  images: [{
    src: 'Possible_New_Logo_Look_Mikes_Pro_Detail_.png',
    alt: "Mike's Pro Detail mobile auto detailing logo",
    notes: 'Hero logo, eager-loaded with fetchPriority="high"'
  }, {
    src: 'Red_BMW_.jpg',
    alt: "Red BMW detailed by Mike's Pro Detail"
  }, {
    src: 'Blue_BMW_SUV.jpg',
    alt: "Blue BMW SUV detailed by Mike's Pro Detail"
  }, {
    src: 'Red_BIG_Truck.jpg',
    alt: "Red truck detailed by Mike's Pro Detail"
  }, {
    src: 'Before_and_After_Gallery_Site.jpg',
    alt: 'Before and after exterior detail - Honda SUV transformation',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'before_and_after.jpg',
    alt: 'Before and after interior detail - deep clean cup holders and floor',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'red_truck.jpg',
    alt: 'Red Ford Raptor full detail - showroom shine exterior',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'water_spots.jpg',
    alt: 'Before and after water spot removal - exterior detail',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'Red_BMW_.jpg (gallery)',
    alt: 'Red BMW M4 convertible full detail - showroom shine',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'engine_cleaning_.jpg',
    alt: 'Engine bay cleaning - 5.7L V8 HEMI spotless engine detail',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'old_mustang.jpg',
    alt: 'Classic blue Ford Mustang 289 full detail - mirror finish exterior detail',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'Old_Porshe.jpg',
    alt: 'Vintage black Porsche 356 Speedster detail - showroom quality exterior and red interior',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'Old_Red_Camaro.jpg',
    alt: 'Classic red Camaro SS 350 full detail - chrome and paint restoration',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'porshe_1956.jpg',
    alt: 'Vintage black Porsche 356 Speedster detail - pristine exterior with red leather interior',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'White_Mustang.jpg',
    alt: 'Classic white Ford Mustang with blue racing stripes - full exterior detail',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'Classic.jpg',
    alt: 'Classic blue and white Chevrolet Corvette convertible full detail - chrome and whitewall tires',
    notes: 'Gallery, lazy-loaded'
  }, {
    src: 'yelp_burst.png',
    alt: 'Yelp',
    notes: 'Platform icon on review cards, lazy-loaded'
  }],
  internalLinks: ['/services/mobile-detailing', '/services/interior-detailing', '/services/exterior-detailing', '/services/full-car-detail', '/services/ceramic-coating', '/services/headlight-restoration', '/services/engine-bay-detailing', '/ventura-county/camarillo', '/ventura-county/oxnard', '/ventura-county/ventura', '/ventura-county/thousand-oaks', '/ventura-county/simi-valley', '/ventura-county/moorpark', '/ventura-county/newbury-park', '/ventura-county/westlake-village', '#services', '#about', '#gallery', '#reviews', '#faq', '#contact', '#contact-form', '#service-areas'],
  externalLinks: ['https://share.google/k4UimfxXqCgBa74jP (Google Reviews)', 'https://www.yelp.com/biz/mikes-pro-detail-agoura (Yelp)', 'https://www.google.com/maps/search/Mike%27s+Pro+Detail+Ventura+County (Google Maps)', 'https://www.bbb.org/search?find_text=Mike%27s+Pro+Detail&find_loc=Ventura+County%2C+CA (BBB)']
};
/* ---------------------------------------------------------------- */
/* SERVICE PAGES                                                     */
/* ---------------------------------------------------------------- */
function servicePageDefaults(path: string) {
  return {
    canonical: `https://www.fulldetailpro.com${path}`,
    robots: 'index, follow, max-image-preview:large, max-snippet:-1',
    openGraph: [{
      label: 'og:type',
      value: 'article'
    }, {
      label: 'og:site_name',
      value: "Mike's Pro Detail"
    }, {
      label: 'og:locale',
      value: 'en_US'
    }, {
      label: 'og:url',
      value: `https://www.fulldetailpro.com${path}`
    }, {
      label: 'og:title / twitter:title',
      value: 'Same as the SEO title above'
    }, {
      label: 'og:description / twitter:description',
      value: 'Same as the meta description above'
    }, {
      label: 'og:image / twitter:image',
      value: 'https://cdn.magicpatterns.com/uploads/8ujQRT1VS2FomWSs5Jvzhk/mikesprodetail2026logo.png'
    }, {
      label: 'twitter:card',
      value: 'summary_large_image'
    }]
  };
}
export const SERVICE_PAGES: ExportPage[] = [{
  id: 'mobile-detailing',
  name: 'Mobile Detailing',
  url: 'https://www.fulldetailpro.com/services/mobile-detailing',
  seoTitle: "Mobile Detailing Ventura County | Mike's Pro Detail",
  metaDescription: "Mobile detailing in Ventura County. Mike's Pro Detail brings interior, exterior & full car detailing to your home or office. 5-star service. Call (818) 381-9905.",
  ...servicePageDefaults('/services/mobile-detailing'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair (@id .../#business) + WebSite + Service "Mobile Auto Detailing" (serviceType: Mobile Car Detailing; areaServed: Ventura County, Camarillo, Oxnard, Ventura, Thousand Oaks, Simi Valley, Moorpark, Newbury Park, Westlake Village) + FAQPage + BreadcrumbList',
  h1: 'Mobile Detailing Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Mobile Detailing'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: 'The detail shop that comes to you. Professional mobile car detailing at your home or office anywhere in Ventura County — no drop-off, no waiting.'
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: "At Mike's Pro Detail, mobile detailing in Ventura County means professional, showroom-quality car care delivered directly to your driveway. There's no need to drop your vehicle off, sit in a waiting room, or rearrange your day around a detail shop's hours. We bring a fully equipped detailing operation — complete with our own water, detailing equipment, and premium products — straight to your home or office, wherever you are in Ventura County. We require access to a standard electrical outlet at the service location."
    }, {
      type: 'h3',
      text: 'H2 — Why Choose Mobile Car Detailing?'
    }, {
      type: 'p',
      text: 'Mobile car detailing in Ventura County combines the highest quality results with unmatched convenience. Instead of automated car washes that scratch and swirl your paint, our technicians hand-detail every vehicle with meticulous care. And because we come to you, you reclaim hours of your day while we transform your car on-site.'
    }, {
      type: 'list',
      items: ['Total Convenience: We detail your car at your home, office, or apartment — you never have to leave.', 'We Bring the Equipment: We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.', 'Premium, Hand-Crafted Results: Safe wash methods and professional-grade products on every vehicle.', 'Flexible Scheduling: Including weekends, to fit your busy Ventura County lifestyle.']
    }, {
      type: 'h3',
      text: 'H2 — Our Mobile Detailing Services'
    }, {
      type: 'p',
      text: 'Every service we offer is performed on-site as part of our mobile detailing operation. Explore each in detail: Interior Detailing, Exterior Detailing, Full Car Detail, Engine Bay Detailing, Headlight Restoration, Ceramic Coating.'
    }, {
      type: 'h3',
      text: 'H2 — Serving All of Ventura County'
    }, {
      type: 'p',
      text: 'Our mobile detailing service reaches every community in Ventura County. Find your city for local details and pricing: Camarillo, Oxnard, Ventura, Thousand Oaks, Simi Valley, Moorpark, Newbury Park, and Westlake Village.'
    }]
  }],
  faqs: [{
    question: 'What is mobile car detailing?',
    answer: "Mobile car detailing is a professional cleaning and restoration service that comes to you. Mike's Pro Detail brings our own water and detailing equipment to your home or office anywhere in Ventura County — no drop-off or waiting room required. We require access to a standard electrical outlet at the service location."
  }, {
    question: 'Do you bring your own water and electricity?',
    answer: 'We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.'
  }, {
    question: 'What areas do you cover for mobile detailing?',
    answer: 'We serve all of Ventura County including Camarillo, Oxnard, Ventura, Thousand Oaks, Simi Valley, Moorpark, Newbury Park, and Westlake Village, plus parts of the San Fernando Valley.'
  }, {
    question: 'How much does mobile detailing cost?',
    answer: 'Exterior details start at $160, interior at $180, and our popular full detail at $300, with adjustments for vehicle size. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'How long does mobile detailing take?',
    answer: 'A wash and wax takes about 1.5–2 hours, interior detailing 2–3 hours, and a full detail 3–4 hours. We never rush — quality results take proper time.'
  }],
  internalLinks: ['/services/interior-detailing', '/services/exterior-detailing', '/services/full-car-detail', '/services/engine-bay-detailing', '/services/headlight-restoration', '/services/ceramic-coating', '/ventura-county/camarillo', '/ventura-county/oxnard', '/ventura-county/ventura', '/ventura-county/thousand-oaks', '/ventura-county/simi-valley', '/ventura-county/moorpark', '/ventura-county/newbury-park', '/ventura-county/westlake-village']
}, {
  id: 'interior-detailing',
  name: 'Interior Detailing',
  url: 'https://www.fulldetailpro.com/services/interior-detailing',
  seoTitle: "Interior Detailing Ventura County | Mike's Pro Detail",
  metaDescription: 'Expert mobile interior detailing in Ventura County — Camarillo, Oxnard, Ventura & Thousand Oaks. Deep cleaning, stain removal & steam sanitizing. Call (818) 381-9905.',
  ...servicePageDefaults('/services/interior-detailing'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair + WebSite + Service "Interior Car Detailing" (areaServed: Camarillo, Thousand Oaks, Ventura County, Los Angeles) + FAQPage + BreadcrumbList',
  h1: 'Interior Detailing Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Interior Detailing'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: "Restore your vehicle's cabin to a factory-fresh feel with our comprehensive mobile interior detailing service. We come directly to your home or office."
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: "Your vehicle's interior is where you spend all your time driving. Over time, dirt, dust, spills, pet hair, and odors can accumulate, making your daily commute less enjoyable and potentially impacting your health. At Mike's Pro Detail, our mobile interior car detailing service is designed to deep clean, sanitize, and restore your cabin to a like-new condition. Whether you're in Camarillo CA, Thousand Oaks, or anywhere in Ventura County, our fully equipped mobile unit brings premium interior detailing directly to your doorstep."
    }, {
      type: 'h3',
      text: 'H2 — What Our Interior Detailing Includes'
    }, {
      type: 'p',
      text: "We don't just wipe down surfaces; we perform a meticulous, multi-step deep cleaning process to ensure every crack and crevice is addressed. Our comprehensive interior detail includes:"
    }, {
      type: 'list',
      items: ['Thorough Vacuuming: Deep extraction of dirt, debris, and pet hair from carpets, seats, floor mats, and the trunk area.', 'Cleaning of fabric seats, carpets, and floor mats to lift deep-set stains and grime.', 'Leather Cleaning & Conditioning: Safe, pH-balanced cleaning of leather seats followed by a premium conditioner to prevent cracking and fading.', 'Steam Cleaning & Sanitization: High-pressure steam is used on vents, cup holders, and high-touch areas to kill bacteria and melt away sticky residue.', 'Plastics & Vinyl Restoration: Cleaning and UV protection applied to the dashboard, door panels, and center console to restore a rich, non-greasy finish.', 'Streak-Free Windows: Interior glass and mirrors are cleaned to a crystal-clear, streak-free shine for optimal visibility.']
    }, {
      type: 'h3',
      text: 'H2 — The Benefits of Professional Interior Detailing'
    }, {
      type: 'p',
      text: "Investing in a professional interior detail goes beyond just aesthetics. A clean interior significantly improves the air quality inside your vehicle by removing dust mites, allergens, and bacteria trapped in the upholstery and ventilation system. Furthermore, regular conditioning of leather and UV protection on plastics prevents premature aging, cracking, and fading caused by the intense Southern California sun. If you're planning to sell your vehicle, a pristine interior is one of the most effective ways to maximize its resale value."
    }, {
      type: 'h3',
      text: "H2 — Why Choose Mike's Pro Detail for Interior Detailing?"
    }, {
      type: 'p',
      text: 'When searching for "Mobile Auto Detailing Camarillo CA" or "Car Detailing Camarillo", you want a service that is reliable, meticulous, and convenient. Mike\'s Pro Detail stands out because we use only premium, eco-friendly products and professional-grade equipment steam cleaners. We understand that your time is valuable, which is why our mobile detailing Ventura County service comes entirely to you—fully equipped with our own water and detailing equipment. We require access to a standard electrical outlet at the service location. From Camarillo, Thousand Oaks, Simi Valley, Woodland Hills, Westlake Village, to Chatsworth, and more we deliver 5-star results without you ever having to leave your home or office.'
    }, {
      type: 'h3',
      text: 'H2 — Related Detailing Services'
    }, {
      type: 'p',
      text: 'Full Car Detail, Exterior Detailing, Mobile Detailing, Headlight Restoration'
    }]
  }],
  faqs: [{
    question: 'What does interior detailing include?',
    answer: 'Our interior detail includes deep vacuuming, shampooing of carpets and fabric seats, leather cleaning and conditioning, steam sanitizing of vents and high-touch surfaces, plastic and vinyl restoration, and streak-free interior glass.'
  }, {
    question: 'How long does interior detailing take?',
    answer: 'Interior detailing typically takes 2–3 hours depending on the size and condition of your vehicle. Heavily soiled interiors or pet hair removal may take longer.'
  }, {
    question: 'Can you remove stains and odors?',
    answer: 'Yes. Our shampooing and steam extraction lift most deep-set stains, and our sanitizing process neutralizes odors from spills, smoke, and pets.'
  }, {
    question: 'How much does interior detailing cost in Ventura County?',
    answer: 'Interior detailing starts at $180, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you come to my home or office?',
    answer: 'Yes — we are fully mobile, bringing our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }],
  internalLinks: ['/services/full-car-detail', '/services/exterior-detailing', '/services/mobile-detailing', '/services/headlight-restoration', 'All 8 city pages (via the shared "Proudly Serving Ventura County" block)']
}, {
  id: 'exterior-detailing',
  name: 'Exterior Detailing',
  url: 'https://www.fulldetailpro.com/services/exterior-detailing',
  seoTitle: "Exterior Detailing Ventura County | Mike's Pro Detail",
  metaDescription: 'Top-rated mobile exterior detailing in Ventura County — Camarillo, Oxnard & Ventura. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905.',
  ...servicePageDefaults('/services/exterior-detailing'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair + WebSite + Service "Exterior Car Detailing" (areaServed: Camarillo, Thousand Oaks, Ventura County, Los Angeles) + FAQPage + BreadcrumbList',
  h1: 'Exterior Detailing Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Exterior Detailing'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: 'Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you.'
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: "Your vehicle's exterior faces a constant barrage of environmental hazards: UV rays, bird droppings, tree sap, road tar, and industrial fallout. Over time, these contaminants degrade your clear coat, leaving your paint looking dull and feeling rough. At Mike's Pro Detail, our mobile exterior car detailing service is designed to safely remove these contaminants, restore a deep gloss, and apply durable protection. Whether you need car detailing in Camarillo, Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a superior alternative to automated car washes that scratch and swirl your paint."
    }, {
      type: 'h3',
      text: 'H2 — Our Exterior Detailing Process'
    }, {
      type: 'p',
      text: 'We utilize safe, scratch-free wash methods and premium products to ensure your vehicle looks its absolute best. Our exterior detailing package includes:'
    }, {
      type: 'list',
      items: ['A meticulous hand wash using premium microfiber towels and pH-neutral soap to safely lift dirt without inducing swirl marks.', 'Deep Wheel & Tire Cleaning: We use specialized brushes and non-acidic cleaners to remove baked-on brake dust from wheel barrels, faces, and tires.', 'Clay Bar Treatment: A crucial step that removes embedded contaminants (like overspray and rail dust) that washing alone cannot remove, leaving the paint smooth as glass.', 'Bug & Tar Removal: Safe breakdown and removal of stubborn bug splatter and road tar from the front bumper and lower rocker panels.', 'Premium Wax / Sealant Application: Application of a high-quality hybrid ceramic wax or polymer sealant to provide months of protection and intense water beading.', 'Tire & Trim Dressing: Application of a no-sling, UV-blocking dressing to tires and faded exterior plastics to restore a rich, dark appearance.']
    }, {
      type: 'h3',
      text: 'H2 — Why Exterior Detailing is Essential'
    }, {
      type: 'p',
      text: "Regular exterior detailing is preventative maintenance for your vehicle's paint. The Southern California sun can quickly cause clear coat failure and oxidation if the paint is left unprotected. By regularly removing bonded contaminants through a clay bar treatment and applying a protective wax or sealant, you create a sacrificial barrier against the elements. This not only keeps your car looking stunning but also preserves its resale value and prevents costly paint repairs down the line."
    }, {
      type: 'h3',
      text: 'H2 — The Premier Choice for Mobile Auto Detailing Camarillo CA'
    }, {
      type: 'p',
      text: 'When you search for "Mobile Detailing Ventura County" or need reliable service in Thousand Oaks, Mike\'s Pro Detail is the trusted choice. We bring our own spot-free water and detailing equipment, and we require access to a standard electrical outlet at the service location, meaning we can perform a flawless exterior detail in your driveway, at your office parking lot, or even at your apartment complex. We treat every vehicle as if it were our own, ensuring that every inch of your exterior receives the attention it deserves.'
    }, {
      type: 'h3',
      text: 'H2 — Related Detailing Services'
    }, {
      type: 'p',
      text: 'Ceramic Coating, Full Car Detail, Mobile Detailing'
    }]
  }],
  faqs: [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For deeper gloss and lasting protection, pair it with our ceramic coating service.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We bring our own spot-free water and detailing equipment. We require access to a standard electrical outlet at the service location.'
  }],
  internalLinks: ['/services/ceramic-coating', '/services/full-car-detail', '/services/mobile-detailing', 'All 8 city pages (shared service-areas block)']
}, {
  id: 'full-car-detail',
  name: 'Full Car Detail',
  url: 'https://www.fulldetailpro.com/services/full-car-detail',
  seoTitle: "Full Car Detail Ventura County | Mike's Pro Detail",
  metaDescription: 'Complete full car detail in Ventura County — Camarillo, Oxnard & Thousand Oaks. Mobile interior deep clean + exterior wax & wax. Call (818) 381-9905.',
  ...servicePageDefaults('/services/full-car-detail'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair + WebSite + Service "Full Car Detail" (areaServed: Camarillo, Thousand Oaks, Ventura County, Los Angeles) + FAQPage + BreadcrumbList',
  h1: 'Full Car Detail Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Full Car Detail'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: 'Our most popular package. A complete bumper-to-bumper transformation combining our premium interior and exterior detailing services.'
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: "If you want your vehicle to look and feel as close to brand new as possible, our Full Car Detail is the ultimate solution. This comprehensive package combines the deep-cleaning power of our interior detailing with the protective, gloss-enhancing benefits of our exterior detailing. At Mike's Pro Detail, we provide top-tier mobile auto detailing in Camarillo CA, Thousand Oaks, and throughout Ventura County, bringing a complete automotive makeover directly to your driveway or workplace."
    }, {
      type: 'h3',
      text: "H2 — What's Included in a Full Detail?"
    }, {
      type: 'p',
      text: 'The Full Detail package leaves no stone unturned. We spend hours meticulously cleaning, restoring, and protecting every surface of your vehicle.'
    }, {
      type: 'h3',
      text: 'H3 — Exterior Services:'
    }, {
      type: 'list',
      items: ['Wash method to safely remove surface dirt and grime.', 'Decontamination & Clay Bar: Removal of embedded iron particles, tree sap, and road tar to leave the paint smooth.', 'Wheel & Tire Detail: Deep cleaning of wheel barrels, faces, and tires, finished with a premium no-sling dressing.', 'Paint Protection: Application of a high-grade hybrid ceramic wax or sealant for months of gloss and UV protection.']
    }, {
      type: 'h3',
      text: 'H3 — Interior Services:'
    }, {
      type: 'list',
      items: ['Deep Vacuuming: Thorough removal of dirt and pet hair, followed steam clean carpets and cloth seats.', 'Leather Treatment: Gentle cleaning and premium conditioning to restore softness and prevent cracking.', 'Surface Sanitization: Steam cleaning of vents, cup holders, and dashboard to kill bacteria and remove sticky residues.', 'Crystal Clear Glass: Streak-free cleaning of all interior and exterior windows and mirrors.']
    }, {
      type: 'h3',
      text: 'H2 — The Ultimate Value and Convenience'
    }, {
      type: 'p',
      text: "Booking a Full Car Detail is the most cost-effective way to completely reset your vehicle's condition. Whether you are preparing to sell your car, returning a lease, or simply want to fall in love with your daily driver all over again, this package delivers unmatched results. Plus, because we are a fully mobile operation, you don't have to waste your day sitting in a waiting room. We bring our own water and detailing equipment, and we require access to a standard electrical outlet at the service location, allowing you to continue working or relaxing at home while we transform your car."
    }, {
      type: 'h3',
      text: 'H2 — Your Local Mobile Detailing Experts'
    }, {
      type: 'p',
      text: 'When residents search for "Car Detailing Camarillo" or "Mobile Detailing Ventura County", they consistently choose Mike\'s Pro Detail for our reliability, attention to detail, and exceptional customer service. From Calabasas to Moorpark, we are dedicated to providing the highest standard of mobile auto detailing available in Southern California.'
    }, {
      type: 'h3',
      text: 'H2 — Related Detailing Services'
    }, {
      type: 'p',
      text: 'Interior Detailing, Exterior Detailing, Engine Bay Detailing, Ceramic Coating'
    }]
  }],
  faqs: [{
    question: 'What is included in a full car detail?',
    answer: 'A full detail combines our complete interior and exterior services: deep vacuuming, shampooing, steam sanitizing, leather conditioning, a safe hand wash, clay bar decontamination, wheel and tire detailing, and premium paint protection.'
  }, {
    question: 'How long does a full detail take?',
    answer: 'A full detail typically takes 3–4 hours depending on the size and condition of your vehicle. We never rush — quality results take proper time and attention.'
  }, {
    question: 'How much does a full car detail cost?',
    answer: 'Our full detail starts at $300, with pricing adjusted for vehicle size and condition. SUVs and trucks are slightly higher. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Is a full detail worth it before selling my car?',
    answer: 'Absolutely. A full detail is one of the most cost-effective ways to maximize resale value, presenting your vehicle in its best possible condition inside and out.'
  }, {
    question: 'Do you come to my home or office for a full detail?',
    answer: 'Yes. We are fully mobile, bringing our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }],
  internalLinks: ['/services/interior-detailing', '/services/exterior-detailing', '/services/engine-bay-detailing', '/services/ceramic-coating', 'All 8 city pages (shared service-areas block)']
}, {
  id: 'ceramic-coating',
  name: 'Ceramic Coating',
  url: 'https://www.fulldetailpro.com/services/ceramic-coating',
  seoTitle: "Ceramic Coating Ventura County | Mike's Pro Detail",
  metaDescription: 'Professional ceramic coating in Ventura County — Camarillo, Oxnard & Thousand Oaks. Long-lasting paint protection & extreme gloss. Call (818) 381-9905.',
  ...servicePageDefaults('/services/ceramic-coating'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair + WebSite + Service "Ceramic Coating" (areaServed: Camarillo, Thousand Oaks, Ventura County, Los Angeles) + FAQPage + BreadcrumbList',
  h1: 'Ceramic Coating Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Ceramic Coating'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: 'Experience the ultimate in paint protection. Ceramic coatings provide years of durable protection, extreme gloss, and effortless maintenance.'
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: "If you want the absolute best protection for your vehicle's paint, traditional waxes and sealants simply cannot compete with a professional . At Mike's Pro Detail, we offer premium hybrid ceramic spray coating application that bond at a molecular level with your vehicle's clear coat. This creates a semi-permanent, glass-like shield that protects against the harsh elements of Southern California. Whether you're driving through Thousand Oaks or parked under the sun in Camarillo CA, our mobile ceramic coating service ensures your vehicle remains protected and incredibly glossy for years."
    }, {
      type: 'h3',
      text: 'H2 — What is a Ceramic Coating?'
    }, {
      type: 'p',
      text: 'A hybrid ceramic coating is a liquid polymer (typically silica-based) that is carefully applied with spray to the exterior of a vehicle. Once cured, it chemically bonds with the factory paint, creating a rigid, hydrophobic (water-repelling) layer of protection. Unlike carnauba wax that melts away in a few weeks, or synthetic sealants that last a few months, a hybrid ceramic coating can last for 1 to 2 years when properly maintained.'
    }, {
      type: 'h3',
      text: 'H2 — The Benefits of Ceramic Coating'
    }, {
      type: 'list',
      items: ['Extreme Hydrophobic Properties: Water aggressively beads up and rolls off the surface, taking dirt and grime with it. This self-cleaning effect makes washing your car incredibly easy.', 'UV Damage & Oxidation Prevention: The coating blocks harmful UV rays, preventing your paint from oxidizing, fading, and looking dull over time.', 'Chemical Resistance: Protects against acidic bird droppings, tree sap, bug splatter, and harsh chemicals that would normally etch into the clear coat.', "Unmatched Candy-Like Gloss: Enhances the reflective properties of your paint, adding depth and clarity that makes your car look like it's encased in glass."]
    }, {
      type: 'h3',
      text: 'H2 — The Application Process'
    }, {
      type: 'p',
      text: 'Applying a hybrid ceramic coating requires preparation. The hybrid ceramic coating will lock in any existing defects. Our process includes a thorough wash, chemical decontamination, and clay bar treatment.'
    }, {
      type: 'h3',
      text: 'H2 — Why Choose Us for Ceramic Coatings?'
    }, {
      type: 'p',
      text: 'Applying a ceramic coating is a highly technical process that requires a controlled environment, patience, and expertise. When searching for "Car Detailing Camarillo" or "Mobile Detailing Ventura County" for high-end protection, Mike\'s Pro Detail is the premier choice. We use only industry-leading, professional-grade coatings and take the necessary time to prep your vehicle correctly. Protect your investment with the ultimate shield—contact us today to discuss the best ceramic coating package for your vehicle.'
    }, {
      type: 'h3',
      text: 'H2 — Related Detailing Services'
    }, {
      type: 'p',
      text: 'Exterior Detailing, Full Car Detail, Mobile Detailing'
    }]
  }],
  faqs: [{
    question: 'How long does a ceramic coating last?',
    answer: 'A properly applied and maintained ceramic coating can last for years, far outlasting traditional wax (weeks) or synthetic sealants (months). Longevity depends on the product tier and how the vehicle is cared for.'
  }, {
    question: 'Does the paint need prep before coating?',
    answer: 'Yes. The paint must be flawless before coating, since the coating locks in any existing defects. Our process includes a thorough wash, iron decontamination, and clay bar treatment before application.'
  }, {
    question: 'What are the benefits of a ceramic coating?',
    answer: 'Ceramic coatings provide extreme hydrophobic water beading, UV and oxidation protection, chemical resistance against bird droppings and sap, and an unmatched candy-like gloss.'
  }, {
    question: 'Can you apply ceramic coating at my home?',
    answer: 'Yes — our mobile service applies ceramic coatings on-site at your home or office in Ventura County, with the controlled prep and care the process requires.'
  }],
  internalLinks: ['/services/exterior-detailing', '/services/full-car-detail', '/services/mobile-detailing', 'All 8 city pages (shared service-areas block)']
}, {
  id: 'headlight-restoration',
  name: 'Headlight Restoration',
  url: 'https://www.fulldetailpro.com/services/headlight-restoration',
  seoTitle: "Headlight Restoration Ventura County | Mike's Pro Detail",
  metaDescription: 'Professional headlight restoration in Ventura County — Camarillo, Oxnard & Thousand Oaks. Remove yellowing & oxidation for clear headlights. Call (818) 381-9905.',
  ...servicePageDefaults('/services/headlight-restoration'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair + WebSite + Service "Headlight Restoration" (areaServed: Camarillo, Thousand Oaks, Ventura County, Los Angeles) + FAQPage + BreadcrumbList',
  h1: 'Headlight Restoration Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Headlight Restoration'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: "Improve your nighttime visibility and enhance your car's appearance by permanently removing yellowing, oxidation, and fog from your headlights."
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: "Are your vehicle's headlights looking cloudy, yellowed, or foggy? Not only does this age the appearance of your car significantly, but it also poses a serious safety hazard by drastically reducing your nighttime visibility. At Mike's Pro Detail, our professional Headlight Restoration service permanently removes heavy oxidation and restores crystal-clear clarity to your lenses. We provide this essential mobile service throughout Camarillo CA, Thousand Oaks, and the greater Ventura County area."
    }, {
      type: 'h3',
      text: 'H2 — Why Do Headlights Turn Yellow?'
    }, {
      type: 'p',
      text: 'Modern vehicle headlights are made of polycarbonate plastic, which is lightweight and impact-resistant. However, this plastic is highly susceptible to UV degradation from the sun. Manufacturers apply a UV-protective clear coat at the factory, but over time, this coating breaks down due to sun exposure, heat from the bulbs, and harsh cleaning chemicals. Once the coating fails, the plastic oxidizes rapidly, resulting in that cloudy, yellow appearance.'
    }, {
      type: 'h3',
      text: 'H2 — Our Professional Restoration Process'
    }, {
      type: 'p',
      text: 'Don\'t be fooled by cheap DIY kits or "wipe-on" fixes that only last a few weeks. We perform a true, multi-step mechanical restoration to permanently fix the issue:'
    }, {
      type: 'list',
      items: ['Taping & Protection: We carefully tape off the surrounding paint and trim to ensure no damage occurs during the sanding process.', 'Wet Sanding: We use progressive grits of specialized sandpaper to completely strip away the failed factory clear coat and the oxidized plastic layer.', 'Machine Compounding: Using a machine polisher and heavy cutting compound, we remove the fine sanding marks and begin restoring clarity.', 'Machine Polishing: A refining polish is used to bring the polycarbonate lens back to a flawless, glass-like transparency.', 'UV Protection Application: This is the most critical step. We apply a durable ceramic coating or specialized UV sealant to protect the bare plastic and prevent it from yellowing again.']
    }, {
      type: 'h3',
      text: 'H2 — Safety and Aesthetics'
    }, {
      type: 'p',
      text: 'Restoring your headlights is one of the most cost-effective ways to instantly improve the look of your vehicle. More importantly, clear headlights allow your bulbs to project light properly, significantly improving your vision at night and during inclement weather, keeping you and your family safe on the road.'
    }, {
      type: 'h3',
      text: 'H2 — Convenient Mobile Service'
    }, {
      type: 'p',
      text: 'Replacing headlight assemblies can cost hundreds or even thousands of dollars. Our restoration service is a fraction of the cost and yields incredible results. If you are searching for "Mobile Detailing Ventura County" or "Car Detailing Camarillo" to fix your cloudy headlights, Mike\'s Pro Detail will come directly to your home or workplace to perform the restoration.'
    }, {
      type: 'h3',
      text: 'H2 — Related Detailing Services'
    }, {
      type: 'p',
      text: 'Full Car Detail, Exterior Detailing, Engine Bay Detailing, Mobile Detailing'
    }]
  }],
  faqs: [{
    question: 'How long does headlight restoration last?',
    answer: 'Because we apply a durable UV sealant or ceramic coating as the final step, our headlight restoration lasts far longer than cheap DIY kits — typically a year or more, depending on sun exposure and care.'
  }, {
    question: 'Why do headlights turn yellow and cloudy?',
    answer: 'Headlight lenses are made of polycarbonate plastic with a factory UV clear coat. Over time that coating breaks down from sun and heat, causing the plastic underneath to oxidize and turn yellow or foggy.'
  }, {
    question: 'Is headlight restoration worth it vs. replacement?',
    answer: 'Yes. Replacing headlight assemblies can cost hundreds or thousands of dollars. Restoration delivers clear, like-new lenses for a fraction of the cost.'
  }, {
    question: 'Does it improve nighttime visibility?',
    answer: 'Significantly. Clear lenses let your bulbs project light properly, improving your vision at night and in poor weather and keeping you safer on the road.'
  }],
  internalLinks: ['/services/full-car-detail', '/services/exterior-detailing', '/services/engine-bay-detailing', '/services/mobile-detailing', 'All 8 city pages (shared service-areas block)']
}, {
  id: 'engine-bay-detailing',
  name: 'Engine Bay Detailing',
  url: 'https://www.fulldetailpro.com/services/engine-bay-detailing',
  seoTitle: "Engine Bay Detailing Ventura County | Mike's Pro Detail",
  metaDescription: "Engine bay detailing in Ventura County. Safe mobile degreasing & cleaning of your engine compartment at your home or office. Call Mike's Pro Detail (818) 381-9905.",
  ...servicePageDefaults('/services/engine-bay-detailing'),
  schema: 'JSON-LD @graph: LocalBusiness/AutoRepair + WebSite + Service "Engine Bay Detailing" (serviceType: Engine Bay Cleaning & Detailing; areaServed: Camarillo, Oxnard, Ventura, Thousand Oaks, Ventura County) + FAQPage + BreadcrumbList',
  h1: 'Engine Bay Detailing Ventura County',
  sections: [{
    heading: 'Breadcrumb',
    blocks: [{
      type: 'p',
      text: 'Home / Services / Engine Bay Detailing'
    }]
  }, {
    heading: 'Subtitle',
    blocks: [{
      type: 'p',
      text: 'Safe, professional engine bay cleaning that comes to you. Restore a clean, presentable engine compartment without risking sensitive components.'
    }]
  }, {
    heading: 'Body copy',
    blocks: [{
      type: 'p',
      text: 'A clean engine bay is the finishing touch on a truly detailed vehicle. Our engine bay detailing in Ventura County safely removes years of grease, dust, and grime from your engine compartment, leaving it clean, presentable, and easier to maintain. We perform this service on-site as part of our mobile operation, serving Camarillo, Oxnard, Ventura, Thousand Oaks, and the surrounding communities.'
    }, {
      type: 'h3',
      text: 'H2 — Our Engine Bay Detailing Process'
    }, {
      type: 'p',
      text: 'Engine bay cleaning requires care and experience. We protect sensitive electrical components, use controlled low-pressure rinsing, and apply the right products for each surface:'
    }, {
      type: 'list',
      items: ['Component Protection: We cover and protect exposed electrical connections and sensitive parts before any cleaning begins.', 'Degreasing: Safe, effective degreasers break down built-up oil, grease, and road grime throughout the bay.', 'Detail Brushing & Rinsing: Hand brushing of tight areas followed by a controlled, low-pressure rinse.', 'Dressing & Protection: A non-greasy dressing restores plastics and hoses to a clean, factory-fresh finish.']
    }, {
      type: 'h3',
      text: 'H2 — Benefits of a Clean Engine Bay'
    }, {
      type: 'p',
      text: "Beyond looking great, a detailed engine bay makes it easier to identify leaks and worn components, helps prevent grime from trapping heat, and significantly boosts your vehicle's resale presentation. It's a smart addition to any full car detail."
    }, {
      type: 'h3',
      text: 'H2 — Related Mobile Detailing Services'
    }, {
      type: 'p',
      text: 'Full Car Detail, Exterior Detailing, Mobile Detailing, Headlight Restoration'
    }]
  }],
  faqs: [{
    question: 'Is engine bay detailing safe for my car?',
    answer: 'Yes. We carefully protect sensitive electrical components and use low-pressure, controlled cleaning with appropriate degreasers. Our experienced technicians know exactly which areas to avoid, keeping your engine safe throughout the process.'
  }, {
    question: 'Why should I get my engine bay detailed?',
    answer: 'A clean engine bay makes it far easier to spot leaks and worn components, helps prevent grime buildup that traps heat, and dramatically improves resale value and presentation when selling your vehicle.'
  }, {
    question: 'How much does engine bay detailing cost in Ventura County?',
    answer: 'Engine bay detailing is often added to a full detail or booked on its own. Pricing depends on the size and condition of the engine bay. Call (818) 381-9905 for a quick quote.'
  }, {
    question: 'Do you come to me for engine bay detailing?',
    answer: 'Yes. Like all our services, engine bay detailing is fully mobile. We bring our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }],
  internalLinks: ['/services/full-car-detail', '/services/exterior-detailing', '/services/mobile-detailing', '/services/headlight-restoration', 'All 8 city pages (shared service-areas block)']
}];
/** Shared blocks that appear on every service page and every city page. */
export const SHARED_SERVICE_PAGE_BLOCKS = {
  serviceAreasHeading: 'Proudly Serving Ventura County',
  serviceAreasBody: 'We provide top-tier mobile auto detailing in Camarillo, Oxnard, Ventura, Thousand Oaks, Simi Valley, Moorpark, Newbury Park, and Westlake Village. Whether you need car detailing in Camarillo or mobile detailing in Ventura County, we come directly to your home or office.',
  ctaHeading: 'Ready to Transform Your Vehicle?',
  ctaBody: 'Book your professional mobile detailing appointment today. We bring the shop to you.',
  ctaButtons: '(818) 381-9905  ·  Get a Free Quote (links to /#contact-form)',
  faqHeading: 'Frequently Asked Questions'
};
/** City-page body sections that are identical in structure across all 8 cities. */
export const CITY_PAGE_TEMPLATE_SECTIONS = ['H2 — Mobile Detailing Services in [City]: "We bring the full range of professional auto detailing to [City], with every service performed on-site at your home or office. Popular options for [City] drivers include:" followed by Interior Detailing, Exterior Detailing, Full Car Detail, and Engine Bay & Headlight Restoration bullet points.', 'H2 — Neighborhoods We Serve in [City]: "Our mobile detailing service reaches every corner of [City], including [neighborhood list]. Wherever you park, we come to you with our own water and detailing equipment. We require access to a standard electrical outlet at the service location."', 'H2 — Explore Our Detailing Services: "Learn more about each of our professional mobile detailing services available in [City] and throughout Ventura County:" followed by links to Interior Detailing, Exterior Detailing, Full Car Detail, Engine Bay Detailing, Headlight Restoration, Mobile Detailing, and Ceramic Coating.', 'Shared "Proudly Serving Ventura County" block and "Ready to Transform Your Vehicle?" CTA block.'];
/* ---------------------------------------------------------------- */
/* 404 PAGE                                                          */
/* ---------------------------------------------------------------- */
export const NOT_FOUND_PAGE = {
  name: '404 — Page Not Found',
  url: 'Any unmatched URL',
  seoTitle: "Page Not Found | Mike's Pro Detail",
  robots: 'noindex, follow',
  h1: 'Page Not Found',
  body: "The page you're looking for doesn't exist. Head back home or explore our mobile detailing services.",
  ctas: 'Back to Home  ·  Our Services'
};
/* ---------------------------------------------------------------- */
/* AI / AEO QUESTION COVERAGE MAP                                    */
/* ---------------------------------------------------------------- */
export const AEO_COVERAGE = [{
  question: 'Who offers mobile car detailing in Ventura County?',
  answeredOn: 'Homepage (About, SEO content block), /services/mobile-detailing, LocalBusiness schema areaServed'
}, {
  question: 'Who offers mobile car detailing in Camarillo / Oxnard / Ventura / Thousand Oaks / Simi Valley / Moorpark / Newbury Park / Westlake Village?',
  answeredOn: 'Dedicated city page for each, plus Service schema with City areaServed'
}, {
  question: 'Who offers mobile car detailing in Woodland Hills?',
  answeredOn: 'Homepage service-areas list, About section, Interior Detailing page (no dedicated city page)'
}, {
  question: 'How much does mobile car detailing cost?',
  answeredOn: 'Homepage pricing section ($90–$350), homepage FAQ, LocalBusiness priceRange'
}, {
  question: 'How much does a full car detail cost?',
  answeredOn: 'Homepage pricing ($300 / $350), /services/full-car-detail FAQ, every city page FAQ'
}, {
  question: 'What is included in a full detail?',
  answeredOn: '/services/full-car-detail (Exterior + Interior service lists), homepage FAQ'
}, {
  question: 'What is included in an interior detail?',
  answeredOn: '/services/interior-detailing, homepage pricing card, homepage FAQ'
}, {
  question: 'Does the detailer come to my home?',
  answeredOn: 'Hero, About, every service page, every city page FAQ'
}, {
  question: 'Does mobile detailing require water or electricity?',
  answeredOn: 'Homepage FAQ, About section, /services/mobile-detailing FAQ'
}, {
  question: 'How long does a car detail take?',
  answeredOn: 'Homepage FAQ, /services/mobile-detailing FAQ, /services/full-car-detail FAQ, city page FAQs'
}, {
  question: 'How do I schedule mobile car detailing?',
  answeredOn: 'Homepage FAQ, contact form, phone CTA in header/hero/footer, "How Our Mobile Detailing Process Works" section'
}];
